import React, { useEffect, useCallback, useContext } from 'react';
import { Button, Badge, Popover, Spin } from 'antd';

import {
  getNotificationsList,
  getNotificationsInfo,
  resetNotificationList,
  resetHeaderNotifications,
  handleHeaderNotificationsVisible,
} from 'redux/reducers/notificationSlice';
import {
  isHeaderNotificationsVisibleSelector,
  isInitialDataLoadedSelector,
  unreadNotificationsCountSelector,
} from 'redux/selectors/notificationSelectors';
import NotificationList from './NotificationList';
import { SocketEvents } from 'utils/socketUtils';
import BaseAlert from 'components/wrappers/BaseAlert';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import NotificationListHeader from './NotificationListHeader';
import { DirectionEnum } from 'api/backofficeNotificationApiClient';
import { closeAllPopupNotifications } from 'utils/notificationUtils';
import { NotificationSocketContext } from '../NotificationSocketProvider';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';
import { isAdminSelector } from 'redux/selectors/authenticationSelectors';

import styles from './HeaderNotifications.module.less';

const NOTIFICATIONS_MAX_COUNT = 999;

export default function HeaderNotifications() {
  const dispatch = useAppDispatch();
  const isInitialDataLoaded = useAppSelector(isInitialDataLoadedSelector);
  const isHeaderNotificationsVisible = useAppSelector(
    isHeaderNotificationsVisibleSelector,
  );
  const unreadNotificationsCount = useAppSelector(
    unreadNotificationsCountSelector,
  );
  const isAdmin = useAppSelector(isAdminSelector);
  const socket = useContext(NotificationSocketContext);

  useEffect(() => {
    isHeaderNotificationsVisible && closeAllPopupNotifications();
  }, [isHeaderNotificationsVisible]);

  useEffect(() => {
    getNotificationList();

    return () => {
      closeAllPopupNotifications();
      dispatch(resetHeaderNotifications());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  useEffect(() => {
    if (!socket) return;

    socket.on(SocketEvents.Connect, () => {
      getNotificationList();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket]);

  const getNotificationList = () => {
    dispatch(resetNotificationList());
    loadNotifications();
  };

  const loadNotifications = async () => {
    const result = await dispatch(getNotificationsInfo(isAdmin));

    if (getNotificationsInfo.fulfilled.match(result)) {
      dispatch(
        getNotificationsList({
          notificationId: result.payload.messageHead as number,
          direction: DirectionEnum.Down,
          isUserAdmin: isAdmin,
        }),
      );
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  const handleVisibleChange = (visible: boolean) => {
    dispatch(handleHeaderNotificationsVisible(visible));
  };

  const hidePopover = useCallback(() => {
    dispatch(handleHeaderNotificationsVisible(false));
  }, [dispatch]);

  const renderContent = () => {
    if (!isInitialDataLoaded) {
      return <Spin />;
    }

    return <NotificationList onNotificationClick={hidePopover} />;
  };

  return (
    <Popover
      content={renderContent()}
      trigger="click"
      placement="bottomRight"
      title={<NotificationListHeader />}
      open={isHeaderNotificationsVisible}
      onOpenChange={handleVisibleChange}
      overlayClassName={styles.notifyPopover}
    >
      <Button
        className={styles.badgeBtn}
        type="link"
        icon={
          <Badge
            count={unreadNotificationsCount}
            overflowCount={NOTIFICATIONS_MAX_COUNT}
          >
            <span className="icon icon-ring" />
          </Badge>
        }
      />
    </Popover>
  );
}
